"use client";

import { useContext, useState } from "react";
import { ReactSVG } from "react-svg";
import classNames from "classnames";
import { useRouter, usePathname } from "next/navigation";
import { RiCommunityLine } from "react-icons/ri";
import { LuChevronsLeft, LuChevronsRight } from "react-icons/lu";
import { IoMdTime } from "react-icons/io";
import {
  MdQrCode,
  MdOutlineQueryStats,
  MdOutlineManageAccounts,
  MdOutlineLogout,
  MdHelpOutline,
  MdOutlineCreditCard,
} from "react-icons/md";

import { Accordion } from "@/components/Accordion";
import { Link } from "@/components/Link";
import { Hamburger } from "@/components/Hamburger";
import { Button } from "@/components/Button";
import { Skeleton } from "@/components/Skeleton";

import { isUserFreeTrial } from "@/services/UserService/UserService";

import { UserContext } from "@/contexts/UserContext";
import { LayoutContext } from "@/contexts/LayoutContext";

import { useLogin } from "@/hooks/auth/useLogin";
import { useAccordion } from "@/hooks/useAccordion";

import { getFreeTrialDaysRemaningContent } from "./utils";
import { paths } from "@/utils/paths";
import { getSiteInfo } from "@/sites";

import { SidebarProps } from "./types";

import "./styles.scss";

export const Sidebar = ({ collapsible, data }: SidebarProps) => {
  const { siteWhiteLogo } = getSiteInfo(process.env.NEXT_PUBLIC_DOMAIN!) || {};

  const router = useRouter();
  const pathname = usePathname();

  const { onHandleLogout } = useLogin();
  const userContext = useContext(UserContext);
  const { user } = userContext;

  const userIsFreeTrial = isUserFreeTrial(userContext);

  const { setIsMenuOpened, isSectionsMenuDisabled = false } = useContext(LayoutContext);

  const { activeItem, toggleOpen } = useAccordion();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isActiveCustomClass = (path: string) => (pathname === path ? "active" : "");

  const classnames = classNames({
    isCollapsed: isCollapsed,
  });

  const renderLogo = () => {
    const classnames = classNames(`sidebar__logo-img`, {
      isCollapsed: isCollapsed,
    });

    if (isCollapsed) {
      return (
        <img
          className={classnames}
          src="/images/sidebar-collapsed.png"
        />
      );
    } else {
      return (
        <ReactSVG
          className="sidebar__logo"
          src={siteWhiteLogo || "/images/favicon-default.png"}
        />
      );
    }
  };

  const renderUserEmail = () => {
    if (!isCollapsed) {
      return (
        <span
          className="sidebar__logout__user"
          data-qa="sidebar-user-email-text"
          title={user?.email}>
          {user?.email ? (
            user.email
          ) : (
            <Skeleton
              dataQA="loader"
              width="10rem"
              height=".8rem"
              backgroundColor="#051a2e00"
            />
          )}
        </span>
      );
    }

    return null;
  };

  const renderCollapseButton = () => {
    const classnames = classNames(`sidebar__collapse-button`, {
      isCollapsed: isCollapsed,
    });

    if (collapsible) {
      return (
        <Button
          icon={!isCollapsed ? <LuChevronsLeft /> : <LuChevronsRight />}
          ghost
          className={classnames}
          onClick={() => {
            setIsCollapsed((prev) => !prev);
          }}
        />
      );
    }

    return null;
  };

  const navigateTo = (path: string) => {
    const bodyElement = document.querySelector("body");
    const htmlElement = document.querySelector("html");
    bodyElement.classList.remove("overflowHidden");
    htmlElement.classList.remove("overflowHidden");
    setIsMenuOpened(false);

    router.push(path);
  };

  return (
    <div
      data-testid="sidebar"
      className={`sidebar ${classnames}`}>
      <Link
        data-testid="sidebar-logo"
        className="sidebar__logo"
        href={paths.myQrCodes()}
        onClick={() => navigateTo(paths.myQrCodes())}>
        {renderLogo()}
      </Link>
      <Hamburger customClass="dashboard">
        <div className="sidebar__hamburger">
          <div className="sidebar__top">
            <Button
              onClick={() => navigateTo(paths.myQrCodes())}
              dataQA="sidebar-my-qr-codes-button"
              iconLeft={!isCollapsed}
              iconTop={isCollapsed}
              icon={<MdQrCode />}
              spacing={2}
              block
              label={data.sidebarQRCodes}
              customClass={isActiveCustomClass(paths.myQrCodes())}
              disabled={isSectionsMenuDisabled}
            />
            <Button
              onClick={() => navigateTo(paths.myQrAnalytics())}
              iconLeft={!isCollapsed}
              iconTop={isCollapsed}
              dataQA="sidebar-my-qr-analytics-button"
              icon={<MdOutlineQueryStats />}
              spacing={2}
              block
              label={data.sidebarAnalytics}
              customClass={isActiveCustomClass(paths.myQrAnalytics())}
              disabled={isSectionsMenuDisabled}
            />
            <Button
              onClick={() => navigateTo(paths.myAccount())}
              iconLeft={!isCollapsed}
              iconTop={isCollapsed}
              dataQA="sidebar-my-account-button"
              icon={<MdOutlineManageAccounts />}
              spacing={2}
              block
              label={data.sidebarAccount}
              customClass={isActiveCustomClass(paths.myAccount())}
              disabled={isSectionsMenuDisabled}
            />
            <Button
              onClick={() => navigateTo(paths.myBilling())}
              iconLeft={!isCollapsed}
              iconTop={isCollapsed}
              dataQA="sidebar-billingt-button"
              icon={<MdOutlineCreditCard />}
              spacing={7}
              block
              label={data.sidebarBilling}
              customClass={isActiveCustomClass(paths.myBilling())}
              disabled={isSectionsMenuDisabled}
            />
            <Accordion
              spacing={0}
              dark
              first
              initialState={false}
              title={data.sidebarCompany}
              icon={<RiCommunityLine />}
              hideArrow={isCollapsed ? true : false}
              iconTop={isCollapsed ? true : false}
              onClick={() => setIsCollapsed((prev) => !prev)}
              sidebarCollapsed={isCollapsed}
              key={1}
              isActiveItem={activeItem === 1}
              toggle={toggleOpen(1)}
              allClosed>
              <ul>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.whoWeAre())}
                    ghost
                    dataQA="sidebar-who-we-are-button"
                    label={data.sidebarWhoWeAre}
                  />
                </li>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.pricing())}
                    ghost
                    dataQA="sidebar-plans-button"
                    label={data.sidebarPrice}
                  />
                </li>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.termsAndConditions())}
                    ghost
                    dataQA="sidebar-terms-and-conditions-button"
                    label={data.sidebarTermsAndConditions}
                  />
                </li>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.termsOfUse())}
                    ghost
                    dataQA="sidebar-terms-of-use-button"
                    label={data.sidebarTermsOfUse}
                  />
                </li>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.privacyPolicy())}
                    ghost
                    label={data.sidebarPrivacyPolicy}
                  />
                </li>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.cookiesPolicy())}
                    ghost
                    dataQA="sidebar-cookies-policy-button"
                    label={data.sidebarCookiesPolicy}
                  />
                </li>
              </ul>
            </Accordion>
            <Accordion
              dark
              initialState={false}
              title={data.sidebarHelp}
              icon={<MdHelpOutline />}
              hideArrow={isCollapsed ? true : false}
              iconTop={isCollapsed ? true : false}
              onClick={() => setIsCollapsed((prev) => !prev)}
              sidebarCollapsed={isCollapsed}
              key={2}
              isActiveItem={activeItem === 2}
              toggle={toggleOpen(2)}
              allClosed>
              <ul>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.contactUs())}
                    ghost
                    dataQA="sidebar-contact-us-button"
                    label={data.sidebarContactUs}
                  />
                </li>
                <li>
                  <Button
                    onClick={() => navigateTo(paths.faq())}
                    ghost
                    dataQA="sidebar-faq-button"
                    label={data.sidebarFaq}
                  />
                </li>
              </ul>
            </Accordion>
          </div>
          <div className="sidebar__bottom">
            {userIsFreeTrial && !isCollapsed && (
              <div className="sidebar__bottom__freeTrial-container">
                <div className="freeTrial_content">
                  <IoMdTime />
                  {getFreeTrialDaysRemaningContent(data, userContext)}
                </div>
                <Button
                  customClass="upgrade-button"
                  onClick={() => navigateTo(paths.pricing())}
                  dataQA="upgrade-sidebar-button"
                  label={data.sidebarUpgrade}
                  rounded={!!data.ctaStyle}
                />
              </div>
            )}
            <div
              className="sidebar__logout"
              data-qa="sidebar-logout-button"
              onClick={() => {
                navigateTo(paths.home());
                setTimeout(() => {
                  onHandleLogout();
                }, 500);
              }}>
              <div className={`sidebar__logout__wrapper ${classnames}`}>
                <div
                  className={`button button--block ${
                    userIsFreeTrial ? "userIsFreeTrial-logout" : ""
                  }`}>
                  <MdOutlineLogout />
                  <span className="logout-label">{data?.sidebarLogout}</span>
                </div>
              </div>
            </div>
            {renderUserEmail()}
            {renderCollapseButton()}
          </div>
        </div>
      </Hamburger>
    </div>
  );
};
